import type { DocumentPageSchema } from '@avisoma/shared';

type SectionBounds = DocumentPageSchema['sectionBounds'];

/**
 * The extracted section as fractions of the parent page, derived from the
 * section's `yTop`/`yBottom` over the persisted parent `pageHeight`. Returns
 * `null` when the parent dimensions weren't persisted (pages segmented before
 * those fields shipped), in which case no band can be drawn. `aspectRatio` lets
 * a caller size the preview box to the page so the image fills it edge-to-edge
 * and a full-width band lines up exactly (LazyImage uses object-contain, which
 * would otherwise letterbox and misalign the band).
 */
export function sectionBandPct(sectionBounds: SectionBounds) {
  const dims = sectionBounds?.pageWidth && sectionBounds?.pageHeight ? sectionBounds : null;
  if (!dims) return null;
  return {
    topPct: Math.max(0, (dims.yTop / dims.pageHeight!) * 100),
    bottomPct: Math.min(100, (dims.yBottom / dims.pageHeight!) * 100),
    aspectRatio: `${dims.pageWidth} / ${dims.pageHeight}`,
  };
}

/**
 * Dims everything outside a section's band and rings the band itself, overlaid on
 * a page preview. The host element must be `relative` and sized to the page (the
 * band is positioned as a percentage of the host's height). Renders nothing when
 * the parent dimensions are unknown. Shared by ParentPageCard (a child showing
 * where it was cut from its parent) and PagePreviewCard (a parent highlighting a
 * hovered child's section).
 */
export function SectionBandOverlay({ sectionBounds, label }: { sectionBounds: SectionBounds; label?: string | null }) {
  const band = sectionBandPct(sectionBounds);
  if (!band) return null;
  return (
    <>
      <div
        className="pointer-events-none absolute inset-x-0 top-0 bg-zinc-900/55"
        style={{ height: `${band.topPct}%` }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 bg-zinc-900/55"
        style={{ height: `${100 - band.bottomPct}%` }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-x-0 ring-2 ring-inset ring-blue-500 bg-blue-400/10"
        style={{ top: `${band.topPct}%`, height: `${band.bottomPct - band.topPct}%` }}
        aria-hidden
        title={label ?? 'Extracted section'}
      />
    </>
  );
}
